import { useRef, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { Fade, AttentionSeeker, Reveal } from 'react-awesome-reveal';
import { useAppSelector } from 'store/config';
import { floatingCss, rotate3D } from 'utils/customAnimation';
import {
  BlockContentWrapper,
  BlockWrapper,
  BlockHeader,
  BlockTitle,
  CUSTOM_BREAK_POINT,
  BackgroundImage,
} from 'components/Blocks/Block.styled';

type Props = {
  id: string;
};

const RoadMap = (props: Props) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const lineRef = useRef<SVGPathElement>(null);

  useEffect(() => {
    const $wrapper = wrapperRef.current;
    const $roadLine = lineRef.current;

    const length = $roadLine.getTotalLength();

    $roadLine.style.strokeDasharray = `${String(length)}px`;
    $roadLine.style.strokeDashoffset = `${String(length)}px`;

    const drawLine = () => {
      const wrapperRect = $wrapper.getBoundingClientRect();

      let scrollPercent = 0;
      const startPoint = wrapperRect.top - window.innerHeight / 1.4;
      if (startPoint <= 0) {
        scrollPercent = Math.abs(startPoint) / wrapperRect.height;

        const draw = length * scrollPercent;

        $roadLine.style.strokeDashoffset = `${String(length - draw)}px`;
      }
    };

    window.addEventListener('scroll', drawLine);

    return () => {
      window.removeEventListener('scroll', drawLine);
    };
  }, []);

  return (
    <PadBlockWrapper id={props.id}>
      <BackgroundImage
        src='/images/background/meteo_left.png'
        top={420}
        right={-50}
        size={218}
      />
      <BackgroundImage
        src='/images/background/meteo_left.png'
        top={680}
        right={150}
        size={118}
      />
      <BlockHeader>
        <BlockTitle>RoadMap</BlockTitle>
      </BlockHeader>
      <ContentWrapper ref={wrapperRef}>
        <svg width='100%' height='100%' style={{ position: 'absolute' }}>
          <Line
            ref={lineRef}
            id='road-line'
            d='m 130 130 l 361 0 l 0 80 l -170 0 l 0 286 l 258 0 l 0 292 l -290 0 l 0 100 l 412 0 l 0 108 l 170 0 l 0 148 l -292 0'
            stroke='white'
            strokeWidth={2}
            fill='none'
          />
        </svg>
        <Rows.Row1>
          <Start>
            <StartTitle>START 2022</StartTitle>
            <StartSubTitle>GUMMUNITY NFT</StartSubTitle>
            <StartImage src='/images/start.png' />
            {/* <Star1 src="/images/roadmap_star.png" />
            <Star2 src="/images/roadmap_star.png" />
            <Star3 src="/images/roadmap_star.png" /> */}
          </Start>
          <Card.Step1>
            <Step>STEP 1</Step>
            <StepTitle>꺼뮤니티 시작</StepTitle>
            <StepContents>
              <ul>
                <StepLi>크립토파티의 주최자로 등장</StepLi>
                <StepLi>꺼뮤니티 (GUMMY + COMMUNITY)의 시작</StepLi>
                <StepLi>NFT 홀더 기본 혜택 공개</StepLi>
              </ul>
            </StepContents>
          </Card.Step1>
        </Rows.Row1>
        <Rows.Row2>
          <Card.Step2>
            <Step>STEP 2</Step>
            <StepTitle>꺼뮤니티 강화</StepTitle>
            <StepContents>
              <ul>
                <StepLi>1차 리빌과 동시에 3개 LEVEL 분급</StepLi>
                <StepLi>INTERACTIVE NFT 특징인 LEVEL UP 가이드 제공</StepLi>
                <StepLi>최종 NFT인 아바타 NFT의 레어티리/혜택 선공개</StepLi>
              </ul>
            </StepContents>
          </Card.Step2>
          <Card.Step3>
            <Step>STEP 3</Step>
            <StepTitle>꺼뮤니티 확장</StepTitle>
            <StepContents>
              <ul>
                <StepLi dense style={{ paddingBottom: 4 }}>
                  <StepMultiLine>NFT를 꾸미기 위한 파츠 NFT 발행</StepMultiLine>
                </StepLi>
                <StepLi dense style={{ paddingBottom: 4 }}>
                  <StepMultiLine>
                    파츠 NFT 기반으로 발전된 취향 기반 꺼뮤니티 등장
                  </StepMultiLine>
                </StepLi>
                <StepLi dense style={{ paddingBottom: 4 }}>
                  <StepMultiLine>
                    취향 꺼뮤니티 DAO의 의사결정에 따른 UTILITY 제공
                  </StepMultiLine>
                </StepLi>
              </ul>
            </StepContents>
          </Card.Step3>
        </Rows.Row2>
        <Rows.Row3>
          <Card.Step4>
            <Step>STEP 4</Step>
            <StepTitle>꺼뮤니티 in 메타버스</StepTitle>
            <StepContents>
              <ul>
                <StepLi>
                  메타버스 내 취향 커뮤니티 GUMMUNITY 전용 공간 제공
                </StepLi>
                <StepLi>메타버스 NFT 토큰 에어드랍</StepLi>
                <StepLi>취향 관련 메타버스 게임 경험 제공</StepLi>
              </ul>
            </StepContents>
          </Card.Step4>
        </Rows.Row3>
      </ContentWrapper>
    </PadBlockWrapper>
  );
};

const PadBlockWrapper = styled(BlockWrapper)`
  @media (max-width: ${CUSTOM_BREAK_POINT}px) {
    padding-left: 36px;
    padding-right: 36px;
  }
`;

const ContentWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: ${CUSTOM_BREAK_POINT}px;
  box-sizing: border-box;

  @media (max-width: ${CUSTOM_BREAK_POINT}px) {
    gap: 64px;
  }
`;

const RowCommon = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;

  @media (max-width: ${CUSTOM_BREAK_POINT}px) {
    align-items: center;
    flex-direction: column;
    gap: 64px;
  }
`;

const Rows = {
  Row1: styled(RowCommon)``,
  Row2: styled(RowCommon)`
    margin-top: 200px;

    @media (max-width: ${CUSTOM_BREAK_POINT}px) {
      margin-top: 0;
    }
  `,
  Row3: styled(RowCommon)``,
};

const CardCommon = styled.div`
  border: 3px solid #ffffff;
  padding: 36px 40px;
  box-sizing: border-box;
  background-color: var(--bg-color);

  width: 100%;
  height: 100%;

  @media (max-width: ${CUSTOM_BREAK_POINT}px) {
    width: 100%;
    height: 100%;
    max-width: 560px;
    max-height: fit-content;
    align-items: center;
    flex-direction: column;
  }
`;

const Card = {
  Step1: styled(CardCommon)`
    max-width: 560px;
    max-height: 295px;
  `,
  Step2: styled(CardCommon)`
    max-width: 580px;
    max-height: 295px;

    /* @media (max-width: ${CUSTOM_BREAK_POINT}px) {
      margin-left: 0;
    } */
  `,
  Step3: styled(CardCommon)`
    max-width: 350px;
    max-height: 360px;
    margin-top: 142px;

    @media (max-width: ${CUSTOM_BREAK_POINT}px) {
      margin-top: 0;
    }
  `,
  Step4: styled(CardCommon)`
    max-width: 580px;
    max-height: 295px;
  `,
};

const Step = styled.p`
  font-size: 22px;
  font-weight: 400;
  padding-bottom: 0.5rem;
`;

const StepTitle = styled.p`
  font-size: 40px;
  font-weight: 600;
  display: inline-block;
  padding-bottom: 20px;
  border-bottom: 2px solid #ffffff;
`;

const StepContents = styled.div`
  margin-top: 1.75rem;
`;

const StepLi = styled.li<{ dense?: boolean }>`
  list-style-type: '· ';
  line-height: ${(props) => (props.dense ? '25px' : '35px')};
  font-size: 20px;
  padding-left: 4px;

  /* &::before {
    content: "· ";
    margin-right: 8px;
  } */
`;

const StepMultiLine = styled.span`
  display: inline-block;
  vertical-align: middle;
`;

const Start = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  width: 260px;
  height: 260px;
  position: relative;

  @media (max-width: ${CUSTOM_BREAK_POINT}px) {
    animation: none;
  }
`;

const StartImage = styled.img`
  width: 100%;
  height: 100%;
  position: absolute;
`;

const Star1 = styled.img`
  position: absolute;
  top: -10px;
  right: -30px;
`;

const Star2 = styled.img`
  position: absolute;
  top: -60px;
  right: 10px;
  scale: 0.8;
  rotate: 7deg;
`;

const Star3 = styled.img`
  position: absolute;
  top: -50px;
  right: -70px;
  scale: 0.5;
  rotate: -20deg;
`;

const StartTitle = styled.p`
  color: var(--bg-color);
  font-size: 33px;
  font-weight: 600;
  font-family: 'Titan One', Arial, Helvetica, sans-serif;
  z-index: 100;
`;
const StartSubTitle = styled.p`
  color: #f8e338;
  font-size: 24px;
  font-weight: 600;
  font-family: 'Titan One', Arial, Helvetica, sans-serif;
  -webkit-text-stroke: 0.1px var(--bg-color);
  z-index: 100;
`;

const Line = styled.path`
  @media (max-width: ${CUSTOM_BREAK_POINT}px) {
    visibility: hidden;
  }
`;

export default RoadMap;
